import {useCallback, useEffect, useRef, useState} from 'react'

/**
 * Tracks whether an element currently intersects the viewport.
 *
 * Written for lazily loading heavy media (video in particular): `hasBeenInViewport`
 * gates the one-time cost of attaching a source, while `isInViewport` gates ongoing
 * playback so off-screen media is paused instead of burning bandwidth and CPU.
 *
 * `rootMargin` defaults to 200px so media starts buffering just before it scrolls
 * into view, which keeps the first visible frame from being blank.
 *
 * When `IntersectionObserver` is unavailable (server render, jsdom, legacy browsers)
 * the element is reported as visible so media is never permanently blocked.
 *
 * @param {Object} [options]
 * @param {string} [options.rootMargin] margin around the root, as in IntersectionObserver
 * @param {number} [options.threshold] visible ratio at which the callback fires
 * @returns {{observeRef: Function, isInViewport: boolean, hasBeenInViewport: boolean}}
 */
export const useInViewport = ({rootMargin = '200px', threshold = 0} = {}) => {
    const [isInViewport, setIsInViewport] = useState(false)
    const [hasBeenInViewport, setHasBeenInViewport] = useState(false)
    const observerRef = useRef(null)

    // Callback ref: fires during commit, so the node is guaranteed to exist.
    const observeRef = useCallback(
        (node) => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
            if (!node) return
            if (typeof IntersectionObserver === 'undefined') {
                setIsInViewport(true)
                setHasBeenInViewport(true)
                return
            }
            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsInViewport(entry.isIntersecting)
                    if (entry.isIntersecting) {
                        setHasBeenInViewport(true)
                    }
                },
                {rootMargin, threshold}
            )
            observer.observe(node)
            observerRef.current = observer
        },
        [rootMargin, threshold]
    )

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
        }
    }, [])

    return {observeRef, isInViewport, hasBeenInViewport}
}

export default useInViewport
